import {
  PropsWithChildren,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  useCallback,
} from 'react';
import { Tooltip } from '@mui/material';
import { Annotation } from '../../Annotation/types';
import MarkElement from './MarkElement';
import { useAnnotationToolbarContext } from '../../Annotation/AnnotationToolbarContext';
import { getToolbarPosition } from '../utils/toolbarUtils';

interface HighlightProps {
  annotation: Annotation;
}

export default function Highlight({
  annotation,
  children,
}: Readonly<PropsWithChildren<HighlightProps>>) {
  const { openToolbarForAnnotation } = useAnnotationToolbarContext();

  const handleClick = useCallback(
    (event: ReactMouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const rect = event.currentTarget.getBoundingClientRect();
      const toolbarPosition = getToolbarPosition(rect);

      openToolbarForAnnotation(toolbarPosition, annotation);
    },
    [annotation, openToolbarForAnnotation],
  );

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const rect = event.currentTarget.getBoundingClientRect();
        const toolbarPosition = getToolbarPosition(rect);

        openToolbarForAnnotation(toolbarPosition, annotation);
      }
    },
    [annotation, openToolbarForAnnotation],
  );

  const markElement = (
    <MarkElement
      annotation={annotation}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {children}
    </MarkElement>
  );

  return annotation.note ? (
    <Tooltip
      title={<div style={{ whiteSpace: 'pre-wrap' }}>{annotation.note}</div>}
      placement='auto'
    >
      {markElement}
    </Tooltip>
  ) : (
    markElement
  );
}
